import React, { useEffect, useMemo, useState } from 'react';
import { LayoutChangeEvent, Platform } from 'react-native';

interface UseDropdownParams<T> {
  options?: Array<{
    label: string;
    value: T;
  }>;
  multiple?: boolean;
  value?: T | T[];
  placeholder?: string;
  onValueChange: (value: T | T[]) => void;
  clearFlag?: boolean;
}

export const useDropdown = <T extends unknown>(params: UseDropdownParams<T>) => {
  const { options, multiple, value, placeholder, onValueChange, clearFlag } = params;
  const isWeb = Platform.OS === 'web';
  const DEFAULT_BUTTON_WIDTH = 210;

  const [dropdownLayout, setDropdownLayout] = useState({
    width: DEFAULT_BUTTON_WIDTH,
    height: 0
  });
  const [isOpen, setIsOpen] = useState(false);
  const [selected, setSelected] = useState(value);
  const data = useMemo(() => {
    return options || [];
  }, [options]);

  useEffect(() => {
    if (!value) return;
    setSelected(value);
  }, [value]);

  useEffect(() => {
    if (!selected) return;
    onValueChange(selected);
  }, [JSON.stringify(selected)]);

  // if not focused, close
  React.useEffect(() => {
    if (!isOpen) {
      clearFlag && setSelected(undefined);
      return;
    }
    const close = () => {
      setIsOpen(false);
    };
    if (isWeb) document.addEventListener('click', close);
    // document.addEventListener('mousedown', close);
    return () => {
      if (isWeb) document.removeEventListener('click', close);
      // document.addEventListener('mousedown', close);
    };
  }, [isOpen]);

  function onLayout(event: LayoutChangeEvent) {
    const { width, height } = event.nativeEvent.layout;
    setDropdownLayout({ width, height });
  }

  function onSelect(value: T) {
    if (multiple) {
      const current = selected === undefined ? [] : Array.isArray(selected) ? selected : [selected];
      const idx = current.indexOf(value);
      if (idx === -1) {
        current.push(value);
      } else {
        current.splice(idx, 1);
      }
      setSelected([...current]);
    } else {
      setSelected(value);
      setIsOpen(false);
    }
  }

  function onToggle() {
    setIsOpen(!isOpen);
  }

  function onClose() {
    setIsOpen(false);
  }

  const displayLabel = useMemo(() => {
    if (selected) {
      if (Array.isArray(selected)) {
        return selected.map((value) => {
          return data.find((datum) => datum.value === value)?.label || value;
        });
      }
      return data.find((datum) => datum.value === selected)?.label || selected;
    }
    if (placeholder) {
      return placeholder;
    }
    return multiple ? [] : '';
  }, [JSON.stringify(selected), data]);

  return {
    isOpen,
    data,
    selected,
    displayLabel,
    dropdownLayout,
    onToggle,
    onLayout,
    onSelect,
    onClose
  };
};
